<svelte:options runes={true} />

<script lang="ts">
  import IconPlus from "~icons/dusk/plus";
  import IconCircle from "~icons/dusk/circle";
  import IconArrowUp from "~icons/dusk/arrow-up";
  import IconArrowRight from "~icons/dusk/arrow-right";
  import IconX from "~icons/dusk/x";
  import IconTag from "~icons/dusk/tag";
  import IconBox from "~icons/dusk/box";
  import type { Snippet } from "svelte";

  export type BadgeSize = "sm" | "md" | "lg";
  export type BadgeIcon =
    "none" | "dot" | "x-close" | "icon-right" | "icon-left" | "icon-only" | "tag" | "box";
  export type BadgeColor =
    | "primary"
    | "warning"
    | "success"
    | "error"
    | "light-sand"
    | "dusty-mauve"
    | "new-green"
    | "dark-green"
    | "blue"
    | "dark-blue"
    | "white"
    | "grey"
    | "charcoal"
    | "red";

  interface Props {
    as?: "div" | "span";
    text?: string;
    size?: BadgeSize;
    icon?: BadgeIcon;
    color?: BadgeColor;
    children?: Snippet;
    class?: string;
    onIconClick?: () => void;
  }

  let {
    as = "div",
    text = "Label",
    size = "sm",
    icon = "none",
    color = "primary",
    children,
    class: className = "",
    onIconClick,
  }: Props = $props();

  const showDot = $derived(icon === "dot");
  const showXClose = $derived(icon === "x-close");
  const showIconRight = $derived(icon === "icon-right");
  const showIconLeft = $derived(icon === "icon-left");
  const showIconOnly = $derived(icon === "icon-only");
  const showTag = $derived(icon === "tag");
  const showBox = $derived(icon === "box");
</script>

<svelte:element
  this={as}
  class={[
    "da-badge",
    size && `da-badge--${size}`,
    color && `da-badge--${color}`,
    icon && `da-badge--icon-${icon}`,
    className,
  ]}
>
  {#if showIconOnly}
    <IconPlus class="da-badge__icon" aria-hidden="true" width="14" height="14" />
  {:else}
    {#if showDot}
      <IconCircle class="da-badge__icon" aria-hidden="true" width="8" height="8" />
    {:else if showIconLeft}
      <IconArrowUp class="da-badge__icon" aria-hidden="true" width="14" height="14" />
    {:else if showTag}
      <IconTag class="da-badge__icon" aria-hidden="true" width="14" height="14" />
    {:else if showBox}
      <IconBox class="da-badge__icon" aria-hidden="true" width="14" height="14" />
    {/if}

    <p class="da-badge__text">
      {#if children}{@render children()}{:else}{text}{/if}
    </p>

    {#if showXClose}
      <button type="button" onclick={onIconClick} aria-label="Remove badge" class="da-badge__x-btn">
        <IconX aria-hidden="true" width="14" height="14" />
      </button>
    {:else if showIconRight}
      <IconArrowRight class="da-badge__icon" aria-hidden="true" width="14" height="14" />
    {/if}
  {/if}
</svelte:element>

<style>
  @layer aurora {
    /* ── Base ── */
    .da-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      box-sizing: border-box;
      white-space: nowrap;
      vertical-align: top;
      color: var(--da-badge-text);
      background-color: var(--da-badge-bg);
      font-weight: var(--da-badge-fw, 700);
    }

    /* ── Size modifiers ── */
    .da-badge--sm {
      border-radius: var(--da-radius-md);
      padding: var(--da-space-px-2) var(--da-space-2);
    }

    .da-badge--md {
      border-radius: var(--da-radius-md);
      padding: var(--da-space-px-2) var(--da-space-px-10);
    }

    .da-badge--lg {
      border-radius: var(--da-radius-md);
      padding: var(--da-space-1) var(--da-space-3);
    }

    .da-badge--icon-icon-only {
      border-radius: var(--da-radius-lg);
      padding: var(--da-space-1);
    }

    .da-badge--icon-dot,
    .da-badge--icon-icon-left,
    .da-badge--icon-tag,
    .da-badge--icon-box {
      padding-left: var(--da-space-px-6);
      gap: var(--da-space-px-6);
    }

    .da-badge--icon-x-close,
    .da-badge--icon-icon-right {
      padding-right: var(--da-space-px-6);
      gap: var(--da-space-1);
    }

    .da-badge--lg.da-badge--icon-dot,
    .da-badge--lg.da-badge--icon-icon-left,
    .da-badge--lg.da-badge--icon-tag,
    .da-badge--lg.da-badge--icon-box {
      padding-left: var(--da-space-px-10);
    }

    .da-badge--lg.da-badge--icon-x-close,
    .da-badge--lg.da-badge--icon-icon-right {
      padding-right: var(--da-space-px-10);
    }

    .da-badge--lg.da-badge--icon-icon-only {
      padding: var(--da-space-2);
    }

    /* ── Color modifiers — maps directly to DUSK brand palette ── */
    .da-badge--primary {
      --da-badge-bg: var(--da-color-brand-sand-500);
      --da-badge-text: var(--da-color-charcoal-500);
      --da-badge-fw: 600;
    }
    .da-badge--warning {
      --da-badge-bg: var(--da-color-mango-50);
      --da-badge-text: var(--da-color-mango-600);
    }
    .da-badge--success {
      --da-badge-bg: var(--da-color-green-50);
      --da-badge-text: var(--da-color-green-700);
    }
    .da-badge--error {
      --da-badge-bg: var(--da-color-vibe-red-50);
      --da-badge-text: var(--da-color-vibe-red-700);
    }
    .da-badge--light-sand {
      --da-badge-bg: var(--da-color-light-sand-500);
      --da-badge-text: var(--da-color-charcoal-500);
    }
    .da-badge--dusty-mauve {
      --da-badge-bg: var(--da-color-dusty-mauve-50);
      --da-badge-text: var(--da-color-dusty-mauve-800);
    }
    .da-badge--new-green {
      --da-badge-bg: var(--da-color-green-500);
      --da-badge-text: var(--da-color-white);
    }
    .da-badge--dark-green {
      --da-badge-bg: var(--da-color-green-300);
      --da-badge-text: var(--da-color-green-900);
    }
    .da-badge--blue {
      --da-badge-bg: var(--da-color-trade-blue-100);
      --da-badge-text: var(--da-color-trade-blue-700);
    }
    .da-badge--dark-blue {
      --da-badge-bg: var(--da-color-trade-blue-500);
      --da-badge-text: var(--da-color-white);
    }
    .da-badge--white {
      --da-badge-bg: var(--da-color-white);
      --da-badge-text: var(--da-color-charcoal-500);
    }
    .da-badge--grey {
      --da-badge-bg: var(--da-color-charcoal-50);
      --da-badge-text: var(--da-color-charcoal-500);
    }
    .da-badge--charcoal {
      --da-badge-bg: var(--da-color-charcoal-500);
      --da-badge-text: var(--da-color-white);
      --da-badge-fw: 600;
    }
    .da-badge--red {
      --da-badge-bg: var(--da-color-red-600);
      --da-badge-text: var(--da-color-white);
    }

    /* ── Elements ── */
    .da-badge__text {
      flex-shrink: 0;
      white-space: nowrap;
      text-align: center;
      margin: 0;
      font-family: var(--da-font-family-primary);
    }

    .da-badge--sm .da-badge__text {
      font-size: var(--da-font-size-xxs);
      line-height: var(--da-line-height-xxs);
    }

    .da-badge--md .da-badge__text {
      font-size: var(--da-font-size-sm);
      line-height: var(--da-line-height-sm);
    }

    .da-badge--lg .da-badge__text {
      font-size: var(--da-font-size-sm);
      line-height: var(--da-line-height-sm);
    }

    .da-badge__counter-text {
      flex-shrink: 0;
      text-align: center;
    }

    :global(.da-badge__icon) {
      flex-shrink: 0;
      display: block;
    }

    :global(.da-badge--icon-dot .da-badge__icon *) {
      fill: currentColor;
      stroke: none;
    }

    .da-badge__x-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      transition:
        color 0.15s,
        transform 0.3s;
      color: var(--da-badge-text);
    }

    .da-badge__x-btn:hover {
      transform: scale(1.3);
      color: color-mix(in srgb, var(--da-badge-text) 85%, var(--da-color-black));
    }

    .da-badge__x-btn:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }

    /* ── Container style query overrides — must stay last in this layer block ── */
    @container style(--da-badge-size: sm) {
      .da-badge {
        border-radius: var(--da-radius-md);
        padding: var(--da-space-px-2) var(--da-space-2);
      }
      .da-badge .da-badge__text {
        font-size: var(--da-font-size-xxs);
        line-height: var(--da-line-height-xxs);
      }
    }

    @container style(--da-badge-size: md) {
      .da-badge {
        border-radius: var(--da-radius-md);
        padding: var(--da-space-px-2) var(--da-space-px-10);
      }
      .da-badge .da-badge__text {
        font-size: var(--da-font-size-sm);
        line-height: var(--da-line-height-sm);
      }
    }

    @container style(--da-badge-size: lg) {
      .da-badge {
        border-radius: var(--da-radius-md);
        padding: var(--da-space-1) var(--da-space-3);
      }
      .da-badge .da-badge__text {
        font-size: var(--da-font-size-sm);
        line-height: var(--da-line-height-sm);
      }
      .da-badge.da-badge--icon-dot,
      .da-badge.da-badge--icon-icon-left,
      .da-badge.da-badge--icon-tag,
      .da-badge.da-badge--icon-box {
        padding-left: var(--da-space-px-10);
      }
      .da-badge.da-badge--icon-x-close,
      .da-badge.da-badge--icon-icon-right {
        padding-right: var(--da-space-px-10);
      }
      .da-badge.da-badge--icon-icon-only {
        padding: var(--da-space-2);
      }
    }
  }
</style>
